import React, { useEffect, useState, useRef } from 'react'

import { InsufficientPermissions, PaginatedTable, PaginatedTableCallbacks } from '../../common'
import { SupplierRecoveryTable } from './recoveryTable'
import { AssignSuppliersRecoveryForm } from './assignSupplierForm'
import defineAbilityFor from './../defineAbility'
import { JsonSupplierRecovery, JsonUser, SupplierRecovery, User } from '../../../models'
import {
    getSupplierRecovery,
    getUsers,
    getUnassignedSupplierRecoveryCount,
} from '../../../api'
import { getAuthUser, Roles, userHasRole } from '../../../helpers'
import { SupplierFilters } from './filters'
import { SuppliersAdvancedSearch } from '..'

export const SupplierRecoveryLanding = () => {
    const ability = defineAbilityFor(getAuthUser())
    const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([])
    const [users, setUsers] = useState<User[]>([])
    const [assignedUserId, setAssignedUserId] = useState<string>('')
    const [sortBy, setSortBy] = useState('balance')
    const [sortDirection, setSortDirection] = useState('desc')
    const updateFiltersRef = useRef<(filterName: string, newValue: string | null, shouldDelete?: boolean) => void>()

    const canView = ability.can('View', 'SupplierRecovery')
    const canAssign = ability.can('Assign', 'Supplier')

    const toggleSupplierSelection = (id: number) => {
        if (selectedSuppliers.includes(id)) {
            setSelectedSuppliers(selectedSuppliers.filter((supplierId) => supplierId !== id))
        } else {
            setSelectedSuppliers([...selectedSuppliers, id])
        }
    }

    const handleSortChange = (field: string) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(field)
            setSortDirection('desc')
        }
    }

    useEffect(() => {
        getUsers()
            .then(json => {
                const usersData: JsonUser[] = json.data
                const parsedUsers: User[] = usersData
                    .map((user: JsonUser) => new User(user))
                    .filter((user: User) => userHasRole(user, Roles.Recovery) || userHasRole(user, Roles.CollectionAgent))
                setUsers(parsedUsers)
            })
            .catch(() => {})
    }, [])
    

    const getSupplierRecoveryByStatus = (params?: { page?: string }) =>
        getSupplierRecovery({ ...params, sortBy, sortDirection })

    if (!canView) {
        return <InsufficientPermissions />
    }

    return (
        <PaginatedTable<SupplierRecovery, JsonSupplierRecovery>
            getData={(params) => getSupplierRecoveryByStatus(params)}
            modelConstructor={(json: JsonSupplierRecovery) => new SupplierRecovery(json)}
            Filters={(props) => {
                updateFiltersRef.current = props.updateFilters
                return <SupplierFilters {...props} />
            }}
            Form={
                canAssign
                    ? (props) => (
                          <div style={{ marginTop: '-18px' }}>
                              <AssignSuppliersRecoveryForm
                                  users={users}
                                  {...props}
                                  getSupplierCount={getUnassignedSupplierRecoveryCount}
                                  selectedSuppliers={selectedSuppliers}
                                  setSelectedSuppliers={setSelectedSuppliers}
                                  assignedUserId={assignedUserId}
                                  onAssignedUserIdChange={setAssignedUserId}
                              />
                          </div>
                      )
                    : undefined
            }
            disableSearch={true}
            AdvancedSearchComponent={SuppliersAdvancedSearch}
            Table={(
                supplierRecovery: SupplierRecovery[],
                callbacks: PaginatedTableCallbacks
            ) => (
                <SupplierRecoveryTable
                    users={users}
                    callbacks={callbacks}
                    resources={{ supplierRecovery }}
                    selectedSuppliers={selectedSuppliers}
                    toggleSupplierSelection={toggleSupplierSelection}
                    setSelectedSuppliers={setSelectedSuppliers}
                />
            )}
            sortOptions={{
                sortBy,
                sortDirection,
                onSortChange: handleSortChange,
                sortFields: [
                    { field: 'name', label: 'Name' },
                    { field: 'balance', label: 'Balance' },
                ],
            }}
            title="Supplier Recovery"
        />
    )
}
